/**
 * router.ts — the History-API router of LabTop Finance (ARCHITECTURE §10: "history API … with
 * server fallback; deep links keep the mockup's query params").
 *
 * Pure functions only: `matchRoute` parses `pathname + search`, `buildPath` builds one. The
 * React binding lives in `hooks/useRouter.tsx`.
 *
 * Routes — the mockup's `#hash` screens one-to-one (spec-business §3.3):
 *   /                  ראשי
 *   /cashflow          תזרים            ?period&match
 *   /invoices          חשבוניות וגבייה  ?status&customer&bucket&range&q&inv
 *   /vat               מע"מ ומסים       ?period
 *   /expenses          הוצאות וספקים    ?cat&noinvoice&period
 *   /loans             הלוואות          ?scope&sort   (REDESIGN §6)
 *   /ops               תפעול LabTop     ?status
 *   /settings/:tab?    הגדרות           (connections | tax | alerts | display | account)
 *   /alerts            מרכז התראות      ?filter
 *   /login             כניסה            ?next
 * An unknown path resolves to `home` (spec §3.3).
 */

export const SCREENS = ['home', 'cashflow', 'invoices', 'vat', 'expenses', 'loans', 'ops', 'settings', 'alerts', 'login'] as const;
export type ScreenName = (typeof SCREENS)[number];

/** Screens whose path carries a second segment. */
const PARAM_SCREENS: ReadonlySet<ScreenName> = new Set<ScreenName>(['settings']);

/** Path segment ↔ screen name (`/` is `home`). */
const SEGMENT_OF: Readonly<Record<ScreenName, string>> = {
  home: '',
  cashflow: 'cashflow',
  invoices: 'invoices',
  vat: 'vat',
  expenses: 'expenses',
  loans: 'loans',
  ops: 'ops',
  settings: 'settings',
  alerts: 'alerts',
  login: 'login',
};

export const SETTINGS_TABS = ['connections', 'tax', 'alerts', 'display', 'account'] as const;
export type SettingsTab = (typeof SETTINGS_TABS)[number];

export interface Route {
  screen: ScreenName;
  /** Second path segment, decoded (`:tab`), or null. */
  param: string | null;
  /** Query string parameters, decoded. */
  query: Readonly<Record<string, string>>;
  /** Canonical `pathname + search` this route was parsed from. */
  path: string;
}

function screenOf(segment: string): ScreenName | null {
  if (segment === '') return 'home';
  for (const s of SCREENS) if (SEGMENT_OF[s] === segment) return s;
  return null;
}

function safeDecode(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/** Parse `?a=1&b=2` (leading `?` optional) into a plain object. */
export function parseQuery(search: string): Record<string, string> {
  const out: Record<string, string> = {};
  const raw = search.startsWith('?') ? search.slice(1) : search;
  if (!raw) return out;
  for (const pair of raw.split('&')) {
    if (!pair) continue;
    const eq = pair.indexOf('=');
    const key = eq < 0 ? pair : pair.slice(0, eq);
    const value = eq < 0 ? '' : pair.slice(eq + 1);
    if (!key) continue;
    out[safeDecode(key)] = safeDecode(value.replace(/\+/g, ' '));
  }
  return out;
}

/** Serialise a query object, dropping empty values; returns `''` or `?a=1&b=2`. */
export function buildQuery(query: Readonly<Record<string, string | number | boolean | null | undefined>>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '' || value === false) continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

/** Parse a `pathname[?search]` (or a full URL) into a Route. Unknown paths resolve to `home`. */
export function matchRoute(url: string): Route {
  let rest = url;
  const hashIndex = rest.indexOf('#');
  if (hashIndex >= 0) rest = rest.slice(0, hashIndex);
  const qIndex = rest.indexOf('?');
  const search = qIndex >= 0 ? rest.slice(qIndex) : '';
  let pathname = qIndex >= 0 ? rest.slice(0, qIndex) : rest;

  // Accept absolute URLs by dropping the origin.
  const schemeMatch = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\/[^/]*/.exec(pathname);
  if (schemeMatch) pathname = pathname.slice(schemeMatch[0].length);
  if (!pathname.startsWith('/')) pathname = `/${pathname}`;

  const segments = pathname.split('/').filter((s) => s.length > 0);
  const query = parseQuery(search);
  const screen = screenOf(safeDecode(segments[0] ?? ''));
  if (!screen) return { screen: 'home', param: null, query, path: buildPath('home', null, query) };

  const param = PARAM_SCREENS.has(screen) && segments[1] ? safeDecode(segments[1]) : null;
  return { screen, param, query, path: buildPath(screen, param, query) };
}

/** Build the canonical path for a screen (`param` is percent-encoded). */
export function buildPath(
  screen: ScreenName,
  param?: string | null,
  query?: Readonly<Record<string, string | number | boolean | null | undefined>>,
): string {
  const segment = SEGMENT_OF[screen];
  let path = segment ? `/${segment}` : '/';
  if (param && PARAM_SCREENS.has(screen)) path += `/${encodeURIComponent(param)}`;
  return path + (query ? buildQuery(query) : '');
}

/** True when two routes render the same screen (used to decide whether to reset scroll). */
export function sameScreen(a: Route, b: Route): boolean {
  return a.screen === b.screen && a.param === b.param;
}

/** The settings tab of a route, defaulting to `connections` (spec §4.7). */
export function settingsTab(route: Route): SettingsTab {
  const fromPath = route.param;
  const fromQuery = route.query.tab;
  const tab = (fromPath ?? fromQuery) as SettingsTab | undefined;
  return tab && (SETTINGS_TABS as readonly string[]).includes(tab) ? tab : 'connections';
}

/**
 * Where to land after a successful sign-in: the `?next=` the router stored when it bounced an
 * unauthenticated deep link, or the home screen.
 *
 * Only same-origin **paths** are accepted: an absolute or protocol-relative value would make the
 * login screen an open redirect, and `/login` itself is refused so a bounce cannot loop.
 */
export function nextAfterLogin(next: string | undefined): string {
  const home = buildPath('home');
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.startsWith('/\\')) return home;
  if (next === '/login' || next.startsWith('/login/') || next.startsWith('/login?')) return home;
  return next;
}
